<template>
  <div class="container">
    <BmHeader :variant="headerVariant">
      <template #logo>
        <a
          v-if="Logo"
          :href="logoLink"
          class="header-logo"
          target="_blank"
          @click="onLogoClick"
        >
          <Logo
            :aria-label="`${$t('generic.logoLabel')} ${$rttI18n.tenantName ?? 'GLS'}`"
            :class="['logo', themeStore.themeId]"
          />
        </a>
      </template>
      <template #language-selector>
        <LocaleSelect
          :variant="languageSelectorVariant"
          class="header-language-selector"
        />
      </template>
    </BmHeader>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue';

import { sendAnalytics } from '@/services/analytics.js';

import { useThemeStore } from '@/stores/theme.js';
import LocaleSelect from '@/v2/components/common/LocaleSelect.vue';
import BmHeader from '@/v2/design-system/components/Header/BmHeader.vue';

import { useTrackingDataStore } from '@/stores/trackingData.js';
import GlsLogo from '@/v2/design-system/assets/logos/GlsLogo.vue';
import BettermileLogo from '@/v2/design-system/assets/logos/BettermileLogo.vue';
import RoyalMailLogo from '@/v2/design-system/assets/logos/RoyalMailLogo.vue';

const themeStore = useThemeStore();
const { data } = useTrackingDataStore();

const $rttI18n = inject('rttI18n');
const $t = inject('t');

const headerVariant = computed(() =>
  ['gls-it', 'gls-pl', 'gls-one'].includes(themeStore.themeId)
    ? 'blue'
    : 'light'
);

const languageSelectorVariant = computed(() => {
  switch (themeStore.themeId) {
    case 'gls-it':
    case 'gls-pl':
    case 'gls-one':
      return 'light';
    case 'bettermile':
    case 'royalmail-uk':
      return 'dark';
    default:
      return 'blue';
  }
});

const Logo = computed(() => {
  switch (themeStore.themeId) {
    case 'bettermile':
      return BettermileLogo;
    case 'royalmail-uk':
      return RoyalMailLogo;
    default:
      return GlsLogo;
  }
});

const logoLink = computed(() =>
  $rttI18n.tenantSpecificWithFallback('header.logoLink')
);

const onLogoClick = () => {
  sendAnalytics(data, 'clicksStats', `${themeStore.themeId}-logo-click`, {
    isV2: true
  });
};
</script>

<style scoped>
.container {
  position: sticky;
  top: 0;

  z-index: 10;
}

.logo {
  height: var(--icon-size-i-24);
  color: var(--blue-gls);

  &.gls-it,
  &.gls-pl,
  &.gls-one {
    color: var(--neutral-text-icon-negative);
  }

  &.royalmail-uk {
    height: 50px;
    margin: -5px 0;
  }

  &.bettermile {
    color: var(--grey-1000);
  }
}

a.header-logo {
  display: inline-flex;
}

.header-language-selector :deep(.dropdown-menu) {
  right: calc(var(--pad-space-sp-16) * -1);
}

@media (max-width: 370px) {
  .header-language-selector :deep(.dropdown-input) {
    max-width: 38vw;
    text-overflow: ellipsis;
  }
}

@media (371px <= width <= 470px) {
  .header-language-selector :deep(.dropdown-input) {
    max-width: 44vw;
    text-overflow: ellipsis;
  }
}

a {
  text-decoration: none;

  &:visited {
    color: inherit;
  }
}
</style>
